import React, { createContext, useContext, useCallback, useMemo } from 'react'
import { useWatchLaterBatch } from 'app/hooks/use-watch-later-batch'
import { useToggleWatchLater } from 'app/hooks/use-toggle-watch-later'
import { useQueryClient } from 'react-query'

interface WatchLaterContextValue {
  isInWatchLater: (id: string) => boolean
  toggleWatchLater: (id: string) => Promise<void>
  isMutating: (id: string) => boolean
  loading: boolean
}

const WatchLaterContext = createContext<WatchLaterContextValue | null>(null)

interface WatchLaterProviderProps {
  children: React.ReactNode
  contentIds: string[]
  keepItemsOnRemove?: boolean
}

export const WatchLaterProvider: React.FC<WatchLaterProviderProps> = ({
  children,
  contentIds,
  keepItemsOnRemove = false,
}) => {
  const { watchLaterIds, isInWatchLater, refetch, loading } =
    useWatchLaterBatch(contentIds)
  const {
    toggleWatchLater: toggleMutation,
    isMutating,
  } = useToggleWatchLater({ skipListInvalidation: keepItemsOnRemove })
  const queryClient = useQueryClient()

  const toggleWatchLater = useCallback(
    async (id: string) => {
      const queryKey = ['watch-later-batch', contentIds]
      const fallbackIds = watchLaterIds ?? []
      const previousIds =
        queryClient.getQueryData<string[]>(queryKey) ?? fallbackIds
      const isCurrentlyInWatchLater = previousIds.includes(id)
      const nextIds = isCurrentlyInWatchLater
        ? previousIds.filter((item) => item !== id)
        : [...previousIds, id]

      queryClient.setQueryData(queryKey, nextIds)
      try {
        await toggleMutation(id)
        await refetch()
      } catch (error) {
        queryClient.setQueryData(queryKey, previousIds)
        throw error
      }
    },
    [toggleMutation, refetch, contentIds, watchLaterIds, queryClient],
  )

  const value = useMemo<WatchLaterContextValue>(
    () => ({
      isInWatchLater,
      toggleWatchLater,
      isMutating,
      loading,
    }),
    [isInWatchLater, toggleWatchLater, isMutating, loading],
  )

  return (
    <WatchLaterContext.Provider value={value}>
      {children}
    </WatchLaterContext.Provider>
  )
}

export const useWatchLaterContext = () => {
  const context = useContext(WatchLaterContext)
  if (!context) {
    throw new Error(
      'useWatchLaterContext must be used within WatchLaterProvider',
    )
  }
  return context
}
